import React from 'react';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { useWeb3 } from '../context/Web3Context';

import { getPublicOrigin } from '../lib/publicUrl';

const APP_URL = getPublicOrigin();
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Telegram share button — opens t.me/share/url with a pre-filled message.
 * Auto-appends the connected wallet's MLM referral code (?ref=XXXX) for viral recruitment.
 *
 * Props:
 *  - title: short title of the item to share (e.g., bet description)
 *  - subtitle: optional second line (cote, viral score, category)
 *  - path: app path to deep-link to (e.g., /bet/abc123, /ai-bets)
 *  - tone: 'green' | 'cyan' | 'gold' (visual variant)
 *  - size: 'sm' | 'icon' | 'md'
 */
export default function TelegramShareButton({
    title,
    subtitle = '',
    path = '/',
    tone = 'cyan',
    size = 'sm',
    label = 'Telegram',
    testId,
}) {
    const { address } = useWeb3();
    const [refCode, setRefCode] = React.useState(null);

    React.useEffect(() => {
        if (!address) return;
        let cancelled = false;
        fetch(`${API}/api/sponsor/profile/${address}`)
            .then(r => r.json())
            .then(d => { if (!cancelled && d.is_sponsor && d.referral_code) setRefCode(d.referral_code); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [address]);

    const buildUrl = () => {
        const refQuery = refCode ? `?ref=${encodeURIComponent(refCode)}` : '';
        const fullUrl = `${APP_URL}${path}${refQuery}`;
        const lines = [
            title,
        ];
        if (subtitle) lines.push(subtitle);
        lines.push('');
        lines.push('Bet4Peer — P2P betting on Base, AI-powered by Grok');
        if (refCode) lines.push('Use my ref code for bonuses');
        const text = lines.join('\n');
        return `https://t.me/share/url?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(text)}`;
    };

    const tones = {
        cyan: 'bg-[#00FFFF]/10 text-[#00FFFF] border-[#00FFFF]/30 hover:bg-[#00FFFF]/20',
        green: 'bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30 hover:bg-[#00FF66]/20',
        gold: 'bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30 hover:bg-[#FFD700]/20',
    };

    const sizing = size === 'icon'
        ? 'h-9 w-9 p-0'
        : size === 'md'
            ? 'h-10 px-4'
            : 'h-9 px-3 text-xs';

    const handleShare = async () => {
        // Open Telegram share dialog
        window.open(buildUrl(), '_blank', 'noopener,noreferrer');
        // Log the share event (fire and forget)
        try {
            await fetch(`${API}/api/social-sharing/share`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sharer_address: address || null,
                    bet_id: testId?.replace(/^share-(ai-bet|bet|jackpot|.+?)-/, '') || null,
                    channel: 'telegram',
                }),
            });
        } catch (e) { /* non-blocking */ }
    };

    return (
        <Button
            variant="outline"
            onClick={handleShare}
            className={`${sizing} ${tones[tone] || tones.cyan} border font-medium gap-1.5`}
            data-testid={testId || 'telegram-share-btn'}
            aria-label="Partager sur Telegram"
            title={refCode ? `Partager avec votre code ${refCode}` : 'Partager sur Telegram'}
        >
            <Send className="w-3.5 h-3.5" />
            {size !== 'icon' && <span>{label}</span>}
        </Button>
    );
}
